import {
  createSelector,
  createSelectorCreator,
  defaultMemoize,
} from 'reselect';
import isequal from 'lodash.isequal';

import { getDateRangeReducer } from 'app/dealer/shared/modules/dealersInsights/redux/dateRange/selectors';
import {
  selectCurrentLevel,
  selectCurrentDealerId,
} from 'app/dealer/shared/modules/dealersInsights/redux/level/selectors';
import { DEALERS_INSIGHTS_TIME_DIVISIONS } from 'app/dealer/shared/modules/dealersInsights/api/dealersInsights.api';
import { dealerInsightsDefaultCacheKey } from 'app/dealer/shared/modules/dealersInsights/api/cache';

import { leadsBenchmarkYearlyOverviewInitialState } from '../reducer.initialData';
import { getLeadsBenchmarkReducer } from '../selectors';
import { selectLeadsBenchmarkSelectedYear } from '../general/selectors';

import { YEARLY_OVERVIEW_PREFIX } from './constants';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isequal);

/**
 * @param {number} selectedYear
 * @param {import('../../../../../shared/modules/dealersInsights/redux/level/reducer.defaultState').DealerInsightsLevel} level
 * @param {string} dealerId
 */
export const getLeadsBenchmarkYearlyOverviewCacheDictionaryKey = (
  selectedYear,
  level,
  dealerId,
) =>
  dealerInsightsDefaultCacheKey({
    featurePrefix: YEARLY_OVERVIEW_PREFIX,
    // feature is only used when the year filter is applied
    selectedTimeDivisionFilter: DEALERS_INSIGHTS_TIME_DIVISIONS.YEAR,
    selectedYear,
    level,
    dealerId,
  });

export const selectleadsBenchmarkYearlyOverviewChartLoading = createSelector(
  [getLeadsBenchmarkReducer],
  ({ yearChartLoading }) => yearChartLoading,
);
/**
 * @description
 * main selector for selecting data corresponding to
 * cached dates in the redux store
 */
export const selectLeadsBenchmarkYearlyOverviewDataByDate = createSelector(
  [
    getLeadsBenchmarkReducer,
    selectLeadsBenchmarkSelectedYear,
    selectCurrentLevel,
    selectCurrentDealerId,
  ],
  (state, year, level, dealerId) => {
    return (
      state[
        getLeadsBenchmarkYearlyOverviewCacheDictionaryKey(year, level, dealerId)
      ] || leadsBenchmarkYearlyOverviewInitialState
    );
  },
);

// #region data properties: all previous and current
export const selectPreviousYearlyDataValues = createSelector(
  [selectLeadsBenchmarkYearlyOverviewDataByDate],
  ({ previous }) => previous,
);

export const selectCurrentYearlyDataValues = createSelector(
  [selectLeadsBenchmarkYearlyOverviewDataByDate],
  ({ current }) => current,
);
// #endregion data properties: all previous and current

// #region data properties: all are zero
export const selectAllLeadsBenchmarkYearlyOverviewDataValuesAreZero = createDeepEqualSelector(
  [selectPreviousYearlyDataValues, selectCurrentYearlyDataValues],
  (previous, current) => {
    return (
      Object.values(previous).every(prop =>
        Object.values(prop).every(val => val === 0),
      ) &&
      Object.values(current).every(prop =>
        Object.values(prop).every(val => val === 0),
      )
    );
  },
);
// #endregion data properties: all are zero

export const selectPreviousYearExists = createSelector(
  [getLeadsBenchmarkReducer, getDateRangeReducer],
  ({ selectedYear }, { firstDate }) => {
    const date = new Date(firstDate);
    return selectedYear - date.getFullYear() > 0;
  },
);

export const chartValuesAreNotEqual = (
  prevProps,
  previousYearlyDataValues,
  currentYearlyDataValues,
  selectedYear,
) => {
  return (
    prevProps &&
    (!isequal(previousYearlyDataValues, prevProps.previousYearlyDataValues) ||
      !isequal(currentYearlyDataValues, prevProps.currentYearlyDataValues) ||
      selectedYear !== prevProps.selectedYear)
  );
};
